import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
  DialogFooter,
} from '@/components/atoms';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/atoms/otp';
import { useState, useEffect } from 'react';

interface OTPConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (code: string) => void;
  onSendOTP: () => void;
  isLoading: boolean;
  type: 'phone' | 'email';
  value: string;
}

export const OTPConfirmationDialog = ({
  isOpen,
  onClose,
  onConfirm,
  onSendOTP,
  isLoading,
  type,
  value,
}: OTPConfirmationDialogProps) => {
  const [code, setCode] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);

  const handleSendOTP = () => {
    onSendOTP();
    setIsOtpSent(true);
  };

  useEffect(() => {
    if (!isOpen) {
      setCode('');
      setIsOtpSent(false);
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (code.length === 5) {
      onConfirm(code);
    }
  };

  const title = isOtpSent
    ? `Verify ${type === 'phone' ? 'phone number' : 'email address'}`
    : `Confirm ${type === 'phone' ? 'phone number' : 'email address'} update`;

  const description = isOtpSent
    ? `Please enter the 5-digit code sent to ${value}.`
    : `You are about to change your ${type === 'phone' ? 'WhatsApp number' : 'email address'} to ${value}. Click below to receive a verification code.`;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          setIsOtpSent(false);
          setCode('');
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center">
          <DialogTitle className="text-xl font-bold">{title}</DialogTitle>
          <DialogDescription className="mt-2 text-muted-foreground">
            {description}
          </DialogDescription>
        </DialogHeader>

        {isOtpSent && (
          <div className="flex flex-col items-center justify-center py-4 text-center">
            <InputOTP
              maxLength={5}
              value={code}
              onChange={setCode}
              disabled={isLoading}
            >
              <InputOTPGroup>
                {Array.from({ length: 5 }).map((_, index) => (
                  <InputOTPSlot key={index} index={index} />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>
        )}

        <DialogFooter className="flex flex-col gap-2 sm:flex-col sm:space-x-0">
          {isOtpSent ? (
            <Button
              className="w-full"
              label="Verify & confirm"
              onClick={handleConfirm}
              disabled={code.length < 5}
              isLoading={isLoading}
            />
          ) : (
            <Button
              className="w-full"
              label="Send verification code"
              onClick={handleSendOTP}
              isLoading={isLoading}
            />
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
